import React, { useState, useEffect } from 'react';
import {
  ClipboardDocumentListIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ChartBarIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import ModeratorTopBar from './ModeratorTopBar';
import Moderation from './Moderation';
import ModeratorAnalytics from './ModeratorAnalytics';
import getModerationStats from '../api/moderation/getModerationStats';

const ModeratorDashboard = () => {
  const [currentStatus, setCurrentStatus] = useState('pending');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState({
    pending: 0,
    approved: 0,
    rejected: 0,
    total: 0
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [refreshLoading, setRefreshLoading] = useState(false);

  // Fetch real stats from API
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const statsData = await getModerationStats();
        setStats(statsData);
      } catch (error) {
        console.error("Error fetching stats:", error);
        setStats({
          pending: 0,
          approved: 0,
          rejected: 0,
          total: 0
        });
      }
    };

    fetchStats();
  }, [currentStatus]);

  const handleRefresh = async () => {
    setRefreshLoading(true);
    try {
      const statsData = await getModerationStats();
      setStats(statsData);
    } catch (error) {
      console.error("Error refreshing stats:", error);
    } finally {
      setRefreshLoading(false);
    }
  };

  const navigationItems = [
    {
      id: 'pending',
      name: 'В ожидании',
      icon: ClockIcon,
      count: stats.pending,
      color: 'yellow',
      description: 'Требуют модерации'
    },
    {
      id: 'active',
      name: 'Принятые',
      icon: CheckCircleIcon,
      count: stats.approved,
      color: 'green',
      description: 'Одобренные объявления'
    },
    {
      id: 'rejected',
      name: 'Отклоненные',
      icon: XCircleIcon,
      count: stats.rejected,
      color: 'red',
      description: 'Отклоненные объявления'
    },
    {
      id: 'all',
      name: 'Все объявления',
      icon: DocumentTextIcon,
      count: stats.total,
      color: 'blue',
      description: 'Всего объявлений'
    }
  ];

  const handleLogout = () => {
    localStorage.clear();
    window.location.reload();
  };

  return (
    <div className="h-screen overflow-hidden page-bg flex">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black bg-opacity-80 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-80 overflow-y-auto dark-sidebar transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:relative lg:flex-shrink-0 lg:block ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      }`}>
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-700">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-accent-grad rounded-xl flex items-center justify-center shadow-lg">
                <ClipboardDocumentListIcon className="w-7 h-7 heading" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">Модерация</h1>
                <p className="text-sm text-text-secondary">Панель управления</p>
              </div>
            </div>

            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-2 rounded-lg text-text-secondary hover:text-white hover:bg-bg-primary"
            >
              <XCircleIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Navigation */}
          <div className="flex-1 p-6 overflow-y-auto">
            <h2 className="text-sm font-semibold text-text-secondary uppercase tracking-wide mb-4">
              Навигация
            </h2>

            <nav className="space-y-2">
              {navigationItems.map((item, index) => {
                const Icon = item.icon;
                const isActive = currentStatus === item.id;
                const activeGradientClasses = {
                  yellow: 'from-amber-500 to-orange-500',
                  green: 'from-green-500 to-emerald-500',
                  red: 'from-red-500 to-pink-500',
                  blue: 'from-blue-500 to-purple-500'
                };

                return (
                  <button
                    key={item.id}
                    onClick={() => setCurrentStatus(item.id)}
                    className={`w-full flex items-center justify-between p-4 rounded-lg transition-all duration-200 ${
                      isActive
                        ? 'bg-white dark:bg-gray-700 shadow-lg'
                        : 'bg-transparent hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`}
                    style={{ animationDelay: `${index * 0.05}s` }}
                  >
                    <div className="flex items-center space-x-4">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        isActive
                          ? `bg-gradient-to-r ${activeGradientClasses[item.color]}`
                          : 'bg-gray-100 dark:bg-gray-800'
                      }`}>
                        <Icon className={`w-5 h-5 ${
                          isActive ? 'text-white' : 'text-neutral-600 dark:text-neutral-400'
                        }`} />
                      </div>
                      <div className="text-left">
                        <p className={`text-base font-medium ${
                          isActive ? 'text-neutral-600 dark:text-neutral-200' : 'text-neutral-600 dark:text-neutral-400'
                        }`}>{item.name}</p>
                        <p className={`text-xs ${
                          isActive ? 'text-neutral-600 dark:text-neutral-200' : 'text-neutral-600 dark:text-neutral-400 opacity-75'
                        }`}>{item.description}</p>
                      </div>
                    </div>
                    <span className={`px-3 py-1 text-xs font-bold rounded-full ${
                      isActive
                        ? `bg-gradient-to-r ${activeGradientClasses[item.color]} text-white`
                        : 'bg-gray-100 dark:bg-gray-800 text-neutral-600 dark:text-neutral-400'
                    }`}>
                      {item.count}
                    </span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Quick Actions - Analytics */}
          <div className="p-6 pb-3">
            <button
              onClick={() => setCurrentStatus('analytics')}
              className="flex items-center group transition-opacity hover:opacity-80"
            >
              <div className="relative w-8 h-8 flex items-center justify-center">
                <div
                  className="absolute inset-0 rounded-[8px]"
                  style={{
                    background: "linear-gradient(135deg, #4F45E5 0%, #7C3AED 100%)",
                    WebkitMask:
                      "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                    mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                    WebkitMaskComposite: "xor",
                    maskComposite: "exclude",
                    padding: "1px",
                  }}
                />
                <div
                  className="absolute inset-0 rounded-[8px] opacity-10"
                  style={{
                    background: "linear-gradient(135deg, #4F45E5 0%, #7C3AED 100%)",
                  }}
                />
                <ChartBarIcon
                  className="w-4 h-4 relative z-10"
                  style={{
                    stroke: "url(#analytics_grad)",
                    strokeWidth: "2",
                  }}
                />
              </div>
              <span className="ml-3 font-semibold text-[16px] leading-[24px] bg-gradient-to-r from-[#4F45E5] to-[#7C3AED] bg-clip-text text-transparent">
                Аналитика
              </span>
            </button>
          </div>

          {/* Logout */}
          <div className="p-6 pt-3">
            <button
              onClick={handleLogout}
              className="flex items-center group transition-opacity hover:opacity-80"
            >
              <div className="relative w-8 h-8 flex items-center justify-center">
                <div
                  className="absolute inset-0 rounded-[8px]"
                  style={{
                    background: "linear-gradient(270deg, #FF0077 0%, #FF1A00 100%)",
                    WebkitMask:
                      "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                    mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                    WebkitMaskComposite: "xor",
                    maskComposite: "exclude",
                    padding: "1px",
                  }}
                />
                <div
                  className="absolute inset-0 rounded-[8px] opacity-10"
                  style={{
                    background: "linear-gradient(270deg, #FF0077 0%, #FF1A00 100%)",
                  }}
                />
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  className="relative z-10"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M2.5 9.5L9.5 2.5M2.5 2.5L9.5 9.5"
                    stroke="url(#logout_grad_v3)"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
              <span className="ml-3 font-semibold text-[16px] leading-[24px] bg-gradient-to-r from-[#FF0077] to-[#FF1A00] bg-clip-text text-transparent">
                Выйти
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* SVG Gradients for icons */}
      <svg width="0" height="0" className="absolute">
        <defs>
          <linearGradient id="analytics_grad" x1="0" y1="6" x2="12" y2="6" gradientUnits="userSpaceOnUse">
            <stop stopColor="#4F45E5" />
            <stop offset="1" stopColor="#7C3AED" />
          </linearGradient>
          <linearGradient id="logout_grad_v3" x1="0" y1="6" x2="12" y2="6" gradientUnits="userSpaceOnUse">
            <stop stopColor="#FF0077" />
            <stop offset="1" stopColor="#FF1A00" />
          </linearGradient>
        </defs>
      </svg>

      {/* Main content — айналу тек осы жерде, сайдбар экранға бекітулі */}
      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        {/* Top bar */}
        <ModeratorTopBar
          navigationItems={navigationItems}
          activeTab={currentStatus}
          onOpenSidebar={() => setSidebarOpen(true)}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onRefresh={handleRefresh}
          refreshLoading={refreshLoading}
        />

        {/* Content area */}
        <div className="flex-1 p-6 overflow-auto min-h-0">
          {currentStatus === 'analytics' ? (
            <ModeratorAnalytics />
          ) : (
            <Moderation key={currentStatus} status={currentStatus} />
          )}
        </div>
      </div>
    </div>
  );
};

export default ModeratorDashboard;